import { useState } from 'react';
import type { Supplier } from '@salonomy/types';
import { Button, Card, EmptyState, Pill, Spinner } from '@salonomy/ui';
import { useSuppliers } from '../../data/stock.js';
import { maskPhone } from '../../lib/format.js';
import { SupplierModal } from './SupplierModal.js';
import './stock.css';

/**
 * Suppliers CRUD — a simple list off `useSuppliers` with create/edit and an active toggle (in the
 * modal). A deactivated supplier is kept in the list (soft delete, never erased) so historic product
 * links stay legible.
 */
export function SuppliersPanel() {
  const { data, isLoading, isError } = useSuppliers();
  const [modal, setModal] = useState<{ existing?: Supplier } | null>(null);

  const rows = data ?? [];

  return (
    <Card>
      <div className="stock-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Suppliers
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Who you buy stock from. Linked to products and receive movements.
          </div>
        </div>
        <Button size="sm" onClick={() => setModal({})}>
          Add supplier
        </Button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading suppliers…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load suppliers. Add one to begin.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No suppliers yet"
          description="Add the wholesalers and brands you buy stock from."
          action={
            <Button size="sm" onClick={() => setModal({})}>
              Add supplier
            </Button>
          }
        />
      ) : (
        <div className="settings-list">
          {rows.map((s) => (
            <div key={s.id} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>
                  {s.name}
                  {!s.active ? (
                    <Pill tone="danger" className="mono">
                      Inactive
                    </Pill>
                  ) : null}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {s.contactEmail ?? '—'}
                  {s.contactPhone ? ` · ${maskPhone(s.contactPhone)}` : ''}
                  {s.accountRef ? ` · acct ${s.accountRef}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Button size="sm" variant="ghost" onClick={() => setModal({ existing: s })}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal ? <SupplierModal existing={modal.existing} onClose={() => setModal(null)} /> : null}
    </Card>
  );
}
